/**
 * Messages.js — FadeFlow customer DM with their barber
 *
 * Conversation lives at shops/{shopId}/conversations/{clientId}/messages
 */
import React, { useEffect, useState, useRef } from 'react';
import { auth, db } from '../firebase';
import {
  collection, addDoc, onSnapshot, query,
  orderBy, serverTimestamp, doc, setDoc,
} from 'firebase/firestore';

function phoneKey(phone) {
  const d = (phone || '').replace(/\D/g, '');
  return d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
}

export default function Messages({ shop, shopId, slug }) {
  const phone    = auth.currentUser?.phoneNumber || '';
  const clientId = phoneKey(phone);
  const name     = localStorage.getItem('ff_name') || 'Client';

  const [messages, setMessages] = useState([]);
  const [text,     setText]     = useState('');
  const [sending,  setSending]  = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (!clientId || !shopId) return;

    // Make sure conversation doc exists
    setDoc(doc(db, 'shops', shopId, 'conversations', clientId), {
      clientName:    name,
      clientPhone:   phone,
      lastMessage:   '',
      lastMessageAt: serverTimestamp(),
      unread:        0,
    }, { merge: true });

    // Listen to messages
    const q = query(
      collection(db, 'shops', shopId, 'conversations', clientId, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' }), 100);
    });
    return unsub;
  }, [clientId, shopId, name, phone]);

  async function send() {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setText('');
    try {
      await addDoc(
        collection(db, 'shops', shopId, 'conversations', clientId, 'messages'),
        { text: trimmed, sender: 'client', createdAt: serverTimestamp() }
      );
      await setDoc(doc(db, 'shops', shopId, 'conversations', clientId), {
        clientName:    name,
        clientPhone:   phone,
        lastMessage:   trimmed,
        lastMessageAt: serverTimestamp(),
        unread:        1,
      }, { merge: true });
    } catch (e) {
      console.error(e);
      setText(trimmed);
    } finally {
      setSending(false);
    }
  }

  function formatTime(ts) {
    if (!ts?.toDate) return '';
    return ts.toDate().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  const shopInitial = (shop?.name || 'B').charAt(0).toUpperCase();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px', background: 'var(--card)',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 20,
          background: 'var(--accent-dim)', border: '2px solid var(--accent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontWeight: 800, color: 'var(--accent)',
        }}>
          {shopInitial}
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>
            {shop?.name || 'Your Barber'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--success)' }}>● Available</div>
        </div>
      </div>

      {/* Messages list */}
      <div
        ref={listRef}
        style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 0', display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--subtext)', paddingTop: 60, fontSize: 15 }}>
            Send a message — ask about availability, styles, or anything else 👋
          </div>
        )}
        {messages.map(msg => {
          const isBarber = msg.sender === 'barber';
          return (
            <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isBarber ? 'flex-start' : 'flex-end' }}>
              <div style={{
                maxWidth: '78%', padding: '10px 14px', borderRadius: 18,
                borderBottomLeftRadius:  isBarber ? 4 : 18,
                borderBottomRightRadius: isBarber ? 18 : 4,
                background: isBarber ? 'var(--card)' : 'var(--accent)',
                border: isBarber ? '1px solid var(--border)' : 'none',
              }}>
                <div style={{ fontSize: 15, lineHeight: 1.4, color: isBarber ? 'var(--text)' : '#000', fontWeight: isBarber ? 400 : 600 }}>
                  {msg.text}
                </div>
                <div style={{ fontSize: 11, marginTop: 4, color: isBarber ? 'var(--subtext)' : 'rgba(0,0,0,0.5)', textAlign: isBarber ? 'left' : 'right' }}>
                  {formatTime(msg.createdAt)}
                </div>
              </div>
            </div>
          );
        })}
        <div style={{ height: 16 }} />
      </div>

      {/* Input */}
      <div style={{
        padding: '10px 12px', background: 'var(--card)',
        borderTop: '1px solid var(--border)',
        display: 'flex', gap: 10, alignItems: 'flex-end',
        paddingBottom: 80,
      }}>
        <textarea
          style={{
            flex: 1, background: 'var(--input-bg)', borderRadius: 20,
            border: 'none', padding: '10px 16px',
            color: 'var(--text)', fontSize: 15, resize: 'none',
            maxHeight: 120, outline: 'none', fontFamily: 'inherit', lineHeight: 1.4,
          }}
          placeholder={`Message ${shop?.name || 'your barber'}…`}
          value={text}
          onChange={e => setText(e.target.value)}
          rows={1}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
          }}
        />
        <button
          onClick={send}
          disabled={!text.trim() || sending}
          style={{
            width: 42, height: 42, borderRadius: 21,
            background: 'var(--accent)', border: 'none',
            fontSize: 20, color: '#000', fontWeight: 900,
            opacity: (!text.trim() || sending) ? 0.35 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >↑</button>
      </div>
    </div>
  );
}
